import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, LogOut, RotateCcw, Save, Trash2 } from "lucide-react";
import { API, useSite } from "@/lib/site";
import { DEFAULT_CONTENT } from "@/lib/content";
import { Field, SECTION_LABELS } from "@/components/admin/Field";
import { Logo } from "@/components/Logo";

const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Что-то пошло не так";
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      localStorage.setItem("dari_token", res.data?.token || "");
      onLogin(res.data);
    } catch (ex) {
      setErr(fmtErr(ex));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid min-h-screen place-items-center bg-[#f3eadb] px-5" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-[#b8893b]/30 bg-[#fbf7ef] p-8 shadow-xl">
        <Logo />
        <h1 className="font-display mt-8 text-3xl text-[#1d1712]">Вход в админ-панель</h1>
        <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-6 w-full rounded-xl border border-[#d9c8a8] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b8893b]/30" data-testid="admin-login-email" />
        <input type="password" required placeholder="Пароль" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-3 w-full rounded-xl border border-[#d9c8a8] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b8893b]/30" data-testid="admin-login-password" />
        {err && <p className="mt-3 text-sm text-red-700" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} className="btn-gold mt-6 w-full justify-center disabled:opacity-60" data-testid="admin-login-submit">{busy ? "…" : "Войти"}</button>
        <Link to="/" className="mt-5 block text-center text-xs text-[#8a6424] hover:underline">← На сайт</Link>
      </form>
    </div>
  );
};

const Leads = ({ headers }) => {
  const [leads, setLeads] = useState([]);
  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leads`, { headers, withCredentials: true });
      setLeads(Array.isArray(res.data) ? res.data : []);
    } catch (e) { toast.error(fmtErr(e)); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    await axios.delete(`${API}/leads/${id}`, { headers, withCredentials: true });
    load();
  };
  if (!leads.length) return <p className="text-sm text-[#5b4f43]" data-testid="admin-leads-empty">Заявок пока нет.</p>;
  return (
    <div className="space-y-3" data-testid="admin-leads-list">
      {leads.map((l) => (
        <div key={l.id} className="flex items-start justify-between gap-4 rounded-xl border border-[#e4d6bb] bg-white p-4">
          <div className="text-sm">
            <p className="font-semibold text-[#1d1712]">{l.name} · <a className="text-[#8a6424]" href={`https://wa.me/${l.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer">{l.phone}</a></p>
            <p className="mt-1 text-xs text-[#8a6424]">{new Date(l.created_at).toLocaleString("ru-RU")} · {l.direction || "комплексно"} · {l.lang}</p>
            {l.message && <p className="mt-2 text-[#4a3f34]">{l.message}</p>}
          </div>
          <button onClick={() => del(l.id)} className="rounded p-1.5 text-red-700 hover:bg-red-50" aria-label="delete" data-testid={`admin-lead-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
};

const Notifications = ({ headers }) => {
  const [info, setInfo] = useState(null);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/telegram/status`, { headers, withCredentials: true });
      setInfo(res.data);
    } catch (e) { toast.error(fmtErr(e)); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);
  const start = async () => {
    try {
      const res = await axios.post(`${API}/telegram/link`, {}, { headers, withCredentials: true });
      setLink(res.data);
      window.open(res.data.url, "_blank", "noopener");
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/telegram/verify`, {}, { headers, withCredentials: true });
      toast.success(`Telegram подключён: ${res.data?.name}`);
      setLink(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const unlink = async (id) => {
    await axios.delete(`${API}/telegram/chats/${id}`, { headers, withCredentials: true });
    load();
  };
  if (!info) return <p className="text-sm text-[#5b4f43]">Загрузка…</p>;
  const chats = Array.isArray(info.chats) ? info.chats : [];
  return (
    <div className="space-y-8" data-testid="admin-notifications">
      <div className="rounded-xl border border-[#e4d6bb] bg-white p-5">
        <p className="font-semibold text-[#1d1712]">E-mail</p>
        <p className="mt-1 text-sm text-[#5b4f43]">Каждая новая заявка приходит письмом на <b data-testid="admin-notify-email">{info.email || "—"}</b>. Письмо может попасть в «Спам» — отметьте его как «Не спам».</p>
      </div>
      <div className="rounded-xl border border-[#e4d6bb] bg-white p-5">
        <p className="font-semibold text-[#1d1712]">Telegram {info.bot ? <span className="font-normal text-[#8a6424]">· @{info.bot}</span> : null}</p>
        {!info.configured ? (
          <p className="mt-1 text-sm text-red-700" data-testid="admin-tg-not-configured">Токен бота ещё не добавлен.</p>
        ) : (
          <>
            <p className="mt-1 text-sm text-[#5b4f43]">Заявки приходят во все подключённые чаты. Чтобы подключить: 1) нажмите «Подключить» — откроется бот; 2) нажмите в нём «Start»; 3) вернитесь сюда и нажмите «Проверить».</p>
            <ul className="mt-4 space-y-2">
              {chats.map((c) => (
                <li key={c.chat_id} className="flex items-center justify-between rounded-lg bg-[#f6efe2] px-3 py-2 text-sm" data-testid={`admin-tg-chat-${c.chat_id}`}>
                  <span>{c.name}</span>
                  <button onClick={() => unlink(c.chat_id)} className="text-xs font-semibold text-red-700">Отключить</button>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={start} className="btn-gold !px-5 !py-2" data-testid="admin-tg-link-btn">Подключить Telegram</button>
              {link && <button onClick={verify} disabled={busy} className="btn-ghost !px-5 !py-2" data-testid="admin-tg-verify-btn">{busy ? "…" : "Проверить"}</button>}
            </div>
            {link && <p className="mt-3 text-xs text-[#5b4f43]">Если бот не открылся: <a className="text-[#8a6424] underline" href={link.url} target="_blank" rel="noopener noreferrer">{link.url}</a></p>}
          </>
        )}
      </div>
    </div>
  );
};

const Editor = ({ user, onLogout }) => {
  const { content, reload } = useSite();
  const [draft, setDraft] = useState(content);
  const [tab, setTab] = useState("hero");
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem("dari_token") || ""}` }), []);
  useEffect(() => { setDraft(content); }, [content]);
  const sections = [...Object.keys(DEFAULT_CONTENT), "leads", "notify"];

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, { data: draft }, { headers, withCredentials: true });
      await reload();
      toast.success("Сохранено! Изменения уже на сайте.");
    } catch (e) {
      toast.error(fmtErr(e));
      if (e?.response?.status === 401) onLogout();
    } finally {
      setSaving(false);
    }
  };
  const reset = async () => {
    if (!window.confirm("Вернуть все тексты и фото к исходным? Ваши правки будут удалены.")) return;
    await axios.delete(`${API}/content`, { headers, withCredentials: true });
    await reload();
    setDraft(DEFAULT_CONTENT);
    toast.success("Контент сброшен к исходному");
  };

  return (
    <div className="min-h-screen bg-[#f6efe2]" data-testid="admin-editor">
      <header className="sticky top-0 z-30 flex flex-wrap items-center justify-between gap-3 border-b border-[#e4d6bb] bg-[#fbf7ef]/95 px-5 py-3 backdrop-blur">
        <Logo />
        <div className="flex flex-wrap items-center gap-2">
          <span className="hidden text-xs text-[#5b4f43] md:inline">{user?.email}</span>
          <Link to="/" target="_blank" className="inline-flex items-center gap-1.5 rounded-full border border-[#b8893b]/40 px-3 py-2 text-xs font-semibold" data-testid="admin-view-site"><ExternalLink className="h-3.5 w-3.5" />Сайт</Link>
          <button onClick={reset} className="inline-flex items-center gap-1.5 rounded-full border border-[#b8893b]/40 px-3 py-2 text-xs font-semibold" data-testid="admin-reset-btn"><RotateCcw className="h-3.5 w-3.5" />Сброс</button>
          <button onClick={onLogout} className="inline-flex items-center gap-1.5 rounded-full border border-[#b8893b]/40 px-3 py-2 text-xs font-semibold" data-testid="admin-logout-btn"><LogOut className="h-3.5 w-3.5" />Выйти</button>
          <button onClick={save} disabled={saving} className="btn-gold !px-5 !py-2 disabled:opacity-60" data-testid="admin-save-btn"><Save className="h-4 w-4" />{saving ? "Сохранение…" : "Сохранить"}</button>
        </div>
      </header>
      <div className="mx-auto grid max-w-[1300px] gap-6 p-5 md:grid-cols-[220px_1fr]">
        <nav className="flex gap-2 overflow-x-auto md:sticky md:top-20 md:flex-col md:self-start">
          {sections.map((s) => (
            <button key={s} onClick={() => setTab(s)} className={`shrink-0 rounded-xl px-4 py-2.5 text-left text-sm font-medium transition-colors ${tab === s ? "bg-[#1d1712] text-[#f6ecd9]" : "text-[#2a2119] hover:bg-[#ece0c9]"}`} data-testid={`admin-tab-${s}`}>
              {s === "leads" ? "Заявки с сайта" : s === "notify" ? "Уведомления" : SECTION_LABELS[s] || s}
            </button>
          ))}
        </nav>
        <section className="rounded-2xl border border-[#e4d6bb] bg-[#fbf7ef] p-5 md:p-8">
          <h2 className="font-display mb-6 text-3xl text-[#1d1712]">{tab === "leads" ? "Заявки с сайта" : tab === "notify" ? "Уведомления о заявках" : SECTION_LABELS[tab]}</h2>
          {tab === "notify" ? <Notifications headers={headers} /> : tab === "leads" ? <Leads headers={headers} /> : (
            <Field value={draft?.[tab]} onChange={(v) => setDraft((d) => ({ ...d, [tab]: v }))} path={tab} fieldKey={tab} authHeaders={headers} />
          )}
        </section>
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    document.title = "Admin — Dari Event";
    const token = localStorage.getItem("dari_token");
    axios.get(`${API}/auth/me`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);
  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) { /* ignore */ }
    localStorage.removeItem("dari_token");
    setUser(false);
  };
  if (user === null) return <div className="grid min-h-screen place-items-center bg-[#f3eadb] text-sm text-[#8a6424]">Загрузка…</div>;
  if (!user) return <Login onLogin={setUser} />;
  return <Editor user={user} onLogout={logout} />;
}
